import type { EndpointInfo } from './types'

// Selbe Quelle wie der API-Client (src/lib/api-client.ts), damit das Beispiel
// bei jedem Domainwechsel automatisch mitzieht statt fest verdrahtet zu sein.
const BASE_URL = import.meta.env.VITE_API_BASE_URL as string

/**
 * Generates a curl command from endpoint information
 */
export function generateCurl(
  method: string,
  path: string,
  headers: Record<string, string> = {},
  body?: object
): string {
  const fullUrl = `${BASE_URL}${path}`
  const lines: string[] = [`curl -X ${method} "${fullUrl}"`]

  Object.entries(headers).forEach(([key, value]) => {
    lines.push(`  -H "${key}: ${value}"`)
  })

  if (body) {
    lines.push(`  -d '${JSON.stringify(body)}'`)
  }

  return lines.join(' \\\n')
}

/**
 * Generates default headers for an endpoint based on its security requirements
 */
export function getDefaultHeaders(security?: Array<Record<string, string[]>>): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  // Check if endpoint requires authentication
  if (security && security.length > 0) {
    // Check if it's an empty array (no auth required) or has auth requirements
    const hasAuth = security.some((scheme) => Object.keys(scheme).length > 0)
    if (hasAuth) {
      headers['Authorization'] = 'Bearer {API_TOKEN}'
    }
  }

  return headers
}

/**
 * Formats a path with example parameters for curl display
 */
export function formatPathForDisplay(path: string): string {
  // Replace path parameters with example values
  return path.replace(/\{(\w+)\}/g, ':$1')
}

/**
 * Extracts endpoint info from OpenAPI spec paths
 */
export function extractEndpoints(spec: { paths: Record<string, unknown> }): EndpointInfo[] {
  const endpoints: EndpointInfo[] = []
  // OpenAPI spec uses lowercase method names (get, post, etc.)
  const methods = ['get', 'post', 'put', 'patch', 'delete'] as const

  for (const [path, pathItem] of Object.entries(spec.paths)) {
    const item = pathItem as Record<string, unknown>
    for (const method of methods) {
      if (item[method]) {
        const operation = item[method] as Record<string, unknown>
        endpoints.push({
          method: method.toUpperCase() as EndpointInfo['method'],
          path,
          summary: (operation.summary as string) || '',
          description: operation.description as string | undefined,
          operationId: operation.operationId as string | undefined,
          tags: operation.tags as string[] | undefined,
          parameters: operation.parameters as EndpointInfo['parameters'],
          requestBody: operation.requestBody as EndpointInfo['requestBody'],
          responses: operation.responses as EndpointInfo['responses'],
          security: operation.security as EndpointInfo['security'],
          isPremium: operation['x-premium-required'] as boolean | undefined,
        })
      }
    }
  }

  return endpoints
}
